/**
 * TUVI - Reintegros a clientes y Descuentos por incumplimiento.
 * Pestaña dentro de Gestión de Pagos.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import { UPLOAD_URL } from '../../config';
import AjusteCarteraModal from './AjusteCarteraModal';
import './AjustesCartera.css';

const formatMoney = (v) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v || 0);
const formatDate = (f) => f ? new Date(f + 'T12:00:00').toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

const AjustesCartera = () => {
  const { success, error } = useNotification();
  const [items, setItems] = useState([]);
  const [totales, setTotales] = useState({ total_reintegros: 0, total_descuentos: 0, cantidad: 0 });
  const [loading, setLoading] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [modal, setModal] = useState({ show: false, editando: null });

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const vars = {};
      if (filtroTipo) vars.tipo = filtroTipo;
      if (busqueda.trim()) vars.busqueda = busqueda.trim();
      const d = await client.query(
        `query($tipo: String, $busqueda: String) { carteraAjustes(tipo: $tipo, busqueda: $busqueda) { items { id proyecto_id tipo valor fecha motivo comprobante_archivo comprobante_nombre cliente_nombre usuario_registro proyecto_nombre apartamento } total_reintegros total_descuentos cantidad } }`,
        vars
      );
      const r = d.carteraAjustes || { items: [], total_reintegros: 0, total_descuentos: 0, cantidad: 0 };
      setItems(r.items || []);
      setTotales({ total_reintegros: r.total_reintegros || 0, total_descuentos: r.total_descuentos || 0, cantidad: r.cantidad || 0 });
    } catch (err) { error('Error al cargar los ajustes'); }
    finally { setLoading(false); }
  }, [filtroTipo, busqueda, error]);

  useEffect(() => { cargar(); }, [cargar]);

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar este registro? Esto revierte su efecto en la cartera del proyecto.')) return;
    try {
      const r = await client.mutate(`mutation($id: Int!) { eliminarCarteraAjuste(id: $id) { success message } }`, { id });
      if (r.eliminarCarteraAjuste.success) { success('Eliminado'); cargar(); }
      else error(r.eliminarCarteraAjuste.message);
    } catch (err) { error('Error al eliminar'); }
  };

  return (
    <div className="ajc-view">
      {/* KPIs */}
      <div className="ajc-kpis">
        <div className="ajc-kpi reint">
          <span className="ajc-kpi-label">↩️ Total reintegrado a clientes</span>
          <span className="ajc-kpi-value">{formatMoney(totales.total_reintegros)}</span>
        </div>
        <div className="ajc-kpi desc">
          <span className="ajc-kpi-label">🏷️ Total descuentos por incumplimiento</span>
          <span className="ajc-kpi-value">{formatMoney(totales.total_descuentos)}</span>
        </div>
        <div className="ajc-kpi total">
          <span className="ajc-kpi-label">📄 Registros</span>
          <span className="ajc-kpi-value">{totales.cantidad}</span>
        </div>
      </div>

      {/* Barra de acciones */}
      <div className="ajc-toolbar">
        <input className="ajc-search-input" type="text" value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Buscar por proyecto, apartamento, cliente o motivo..." />
        <select className="ajc-select" value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
          <option value="">Todos</option>
          <option value="reintegro">Reintegros</option>
          <option value="descuento">Descuentos</option>
        </select>
        <button className="ajc-btn-nuevo" onClick={() => setModal({ show: true, editando: null })}>+ Nuevo</button>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="ajc-empty">Cargando...</div>
      ) : items.length === 0 ? (
        <div className="ajc-empty">No hay reintegros ni descuentos registrados.</div>
      ) : (
        <div className="ajc-table-wrap">
          <table className="ajc-table">
            <thead>
              <tr>
                <th>Tipo</th><th>Proyecto</th><th>Cliente</th><th>Fecha</th>
                <th style={{ textAlign: 'right' }}>Valor</th><th>Motivo</th><th style={{ textAlign: 'center' }}>Comp.</th><th></th>
              </tr>
            </thead>
            <tbody>
              {items.map(a => (
                <tr key={a.id}>
                  <td>
                    <span className={`ajc-badge ${a.tipo === 'reintegro' ? 'b-reint' : 'b-desc'}`}>
                      {a.tipo === 'reintegro' ? 'Reintegro' : 'Descuento'}
                    </span>
                  </td>
                  <td><strong>{a.proyecto_nombre || `#${a.proyecto_id}`}</strong>{a.apartamento ? ` · Apto ${a.apartamento}` : ''}</td>
                  <td>{a.cliente_nombre || '-'}</td>
                  <td>{formatDate(a.fecha)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: a.tipo === 'reintegro' ? '#c2410c' : '#b45309' }}>− {formatMoney(a.valor)}</td>
                  <td className="ajc-motivo" title={a.motivo}>{a.motivo}</td>
                  <td style={{ textAlign: 'center' }}>
                    {a.comprobante_archivo
                      ? <a className="ajc-link" href={`${UPLOAD_URL}/catalogo/img/comprobantes/${a.comprobante_archivo}`} target="_blank" rel="noopener noreferrer" title="Ver comprobante">📎</a>
                      : <span className="ajc-muted">—</span>}
                  </td>
                  <td className="ajc-acciones">
                    <button className="ajc-icon" title="Editar" onClick={() => setModal({ show: true, editando: a })}>✏️</button>
                    <button className="ajc-icon danger" title="Eliminar" onClick={() => eliminar(a.id)}>🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AjusteCarteraModal
        show={modal.show}
        editando={modal.editando}
        onClose={() => setModal({ show: false, editando: null })}
        onSaved={cargar}
      />
    </div>
  );
};

export default AjustesCartera;
